import { ENEMIES } from '../../data/enemies';
import { SIM_HZ } from '../../data/tuning';

/**
 * EN-12 Teleférico (GDD §8.3, issue #88): a cabin that goes back and forth along a diagonal cable at a constant
 * speed. The cable crosses the playfield but does not hurt; only the cabin does. The whole thing is fixed in the
 * world and scrolls by like a column. The cable runs from the level's `x`, `y` (lower end) to `params.x2`,
 * `params.y2`; the cabin starts at the lower end unless `params.startT` says otherwise. Pure and deterministic.
 */
export const EN12 = ENEMIES['EN-12'];

/** Per-instance parameters of the level data (`SegmentDef.enemies[].params`). */
export interface CableParams {
  x2?: number;
  y2?: number;
  speed?: number;
  /** Where on the cable the cabin starts, 0 (lower end) to 1 (upper end). */
  startT?: number;
  /** -1 sends the cabin towards the lower end first. */
  direction?: number;
}

export interface CableCarState {
  /** Ticks since it was first active (it keeps counting after it leaves the screen). */
  ticks: number;
}

export const createCableCar = (): CableCarState => ({ ticks: 0 });

export const cableLength = (lower: { x: number; y: number }, params: CableParams): number =>
  Math.hypot((params.x2 ?? lower.x) - lower.x, (params.y2 ?? lower.y) - lower.y);

/** A distance along a back and forth path of length `length`: the triangle wave of the cabin. */
export function pingPong(distance: number, length: number): number {
  if (length <= 0) return 0;
  const cycle = ((distance % (2 * length)) + 2 * length) % (2 * length);
  return cycle <= length ? cycle : 2 * length - cycle;
}

/** The cabin after `ticks` ticks: its point on the cable and the distance from the lower end. */
export function cabinPosition(
  lower: { x: number; y: number },
  params: CableParams,
  ticks: number,
): { x: number; y: number; along: number } {
  const length = cableLength(lower, params);
  const start = (params.startT ?? 0) * length;
  const direction = params.direction === -1 ? -1 : 1;
  const along = pingPong(start + (direction * (params.speed ?? EN12.defaultSpeed) * ticks) / SIM_HZ, length);
  const fraction = length === 0 ? 0 : along / length;
  return {
    x: lower.x + ((params.x2 ?? lower.x) - lower.x) * fraction,
    y: lower.y + ((params.y2 ?? lower.y) - lower.y) * fraction,
    along,
  };
}

export const stepCableCar = (
  state: CableCarState,
  lower: { x: number; y: number },
  params: CableParams = {},
): { x: number; y: number } => {
  state.ticks += 1;
  const { x, y } = cabinPosition(lower, params, state.ticks);
  return { x, y };
};
